import { useState } from "react";
import { useParams } from "react-router";
import { apiFetch } from "@contractor-quoting/api-client";
import { Badge, Button, Card, SecondaryButton } from "@contractor-quoting/shared-ui";
import type { ProjectDetail } from "../pages/Projects.js";
import { QuoteReviewModal, type QuoteFormSubmission } from "./QuoteReviewModal.js";

export function ProjectActionCards({
  project,
  latest,
  reload,
}: {
  project: ProjectDetail;
  latest: ProjectDetail["estimates"][number] | undefined;
  reload: () => Promise<void>;
}) {
  const { projectId = "" } = useParams();
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const [signerName, setSignerName] = useState("");
  const [declineReason, setDeclineReason] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);
  const money = (minor: string, currency = "CAD") =>
    new Intl.NumberFormat("en-CA", { style: "currency", currency }).format(Number(minor) / 100);
  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setActionError("");
    try {
      await action();
      await reload();
    } catch (reason) {
      setActionError(reason instanceof Error ? reason.message : "Project action failed");
    } finally {
      setBusy(false);
    }
  };
  const review = project.intakeReview;
  const saveQuoteForm = async (facts: QuoteFormSubmission[]) => {
    setBusy(true);
    setActionError("");
    try {
      const result = await apiFetch<{
        estimate: Record<string, unknown> | null;
        requiredInformation: Array<{ message: string }>;
      }>(`/v1/customer/projects/${projectId}/review-quote`, {
        method: "POST",
        body: JSON.stringify({ facts }),
      });
      if (!result.estimate) {
        throw new Error(
          result.requiredInformation[0]?.message ?? "One required project value is still missing.",
        );
      }
      await reload();
    } catch (reason) {
      setActionError(reason instanceof Error ? reason.message : "The estimate could not be saved");
      throw reason;
    } finally {
      setBusy(false);
    }
  };
  return (
    <section aria-label="Project actions" className="space-y-4 pt-2">
      {!review && project.intakeState ? (
        <Card className="border-emerald-200 p-5 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-emerald-800">
            Project brief
          </p>
          <h3 className="mt-2 text-lg font-black">Here’s what will be included</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {project.intakeState.workItems.map((scope) => (
              <Badge key={scope.id}>
                {scope.label || scope.canonical_type.replaceAll("_", " ")}
              </Badge>
            ))}
          </div>
          <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
            {project.intakeState.facts.slice(0, 8).map((fact) => (
              <div key={fact.id} className="rounded-lg bg-stone-50 p-2">
                <dt className="font-semibold capitalize text-stone-500">
                  {fact.field.replaceAll("_", " ")}
                </dt>
                <dd className="mt-0.5 font-bold">
                  {String(fact.normalized_value ?? fact.raw_value)}
                </dd>
              </div>
            ))}
          </dl>
          {project.intakeState.accuracyGaps.length ? (
            <p className="mt-3 text-sm text-stone-600">
              A few details are assumed for planning and will be shown before the estimate is saved.
            </p>
          ) : null}
          <div className="mt-4 flex flex-wrap gap-2">
            <SecondaryButton
              onClick={() =>
                document
                  .querySelector<HTMLTextAreaElement>('[aria-label="Project message"]')
                  ?.focus()
              }
            >
              Edit details
            </SecondaryButton>
            <Button disabled={busy} onClick={() => setReviewOpen(true)}>
              Review quote
            </Button>
          </div>
        </Card>
      ) : null}
      {review?.status === "prepared" ? (
        <Card className="border-emerald-200 p-5 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-emerald-800">
            Project review
          </p>
          <h3 className="mt-2 text-lg font-black">Ready for your review</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {review.summary.scopes.map((scope) => (
              <Badge key={scope.id}>{scope.label || scope.type.replaceAll("_", " ")}</Badge>
            ))}
          </div>
          <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
            {review.summary.facts.slice(0, 8).map((fact) => (
              <div key={fact.id} className="rounded-lg bg-stone-50 p-2">
                <dt className="font-semibold capitalize text-stone-500">
                  {fact.field.replaceAll("_", " ")}
                </dt>
                <dd className="mt-0.5 font-bold">{String(fact.value)}</dd>
              </div>
            ))}
          </dl>
          {review.summary.measurements.length ? (
            <div className="mt-3 flex flex-wrap gap-2 text-sm">
              {review.summary.measurements.map((measurement) => (
                <Badge key={measurement.id}>
                  {measurement.type.replaceAll("_", " ")}: {measurement.value} {measurement.unit}
                </Badge>
              ))}
            </div>
          ) : null}
          {review.summary.exclusions.length ? (
            <div className="mt-3 rounded-xl bg-stone-50 p-3 text-sm text-stone-700">
              <p className="font-bold">Not included</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {review.summary.exclusions.map((exclusion) => (
                  <li key={exclusion.requirement_id}>
                    {exclusion.requirement_id.replaceAll("_", " ")}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {review.accuracy_gaps.length ? (
            <div className="mt-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-950">
              <p className="font-bold">Estimate assumptions</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {review.accuracy_gaps.slice(0, 5).map((gap) => (
                  <li key={gap.id}>{gap.message}</li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="mt-4 flex flex-wrap gap-2">
            <SecondaryButton
              onClick={() =>
                document
                  .querySelector<HTMLTextAreaElement>('[aria-label="Project message"]')
                  ?.focus()
              }
            >
              Edit details
            </SecondaryButton>
            <Button disabled={busy} onClick={() => setReviewOpen(true)}>
              Review quote
            </Button>
          </div>
        </Card>
      ) : null}
      {latest ? (
        <Card className="border-emerald-200 p-5 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-emerald-800">
            Estimated project price
          </p>
          <p className="mt-2 text-2xl font-black">
            {latest.price_presentation === "single" || latest.generated_total_minor
              ? money(latest.generated_total_minor ?? latest.total_low_minor, latest.currency)
              : `${money(latest.total_low_minor, latest.currency)}–${money(
                  latest.total_high_minor,
                  latest.currency,
                )}`}
          </p>
          <p className="mt-1 text-sm text-stone-500">Draft quote · Awaiting contractor approval</p>
          {(project.priceActivity?.length ?? 0) > 0 ? (
            <details className="mt-4 rounded-xl bg-stone-50 p-3 text-sm">
              <summary className="cursor-pointer font-black">How your price was built</summary>
              <ol className="mt-3 space-y-2 text-stone-700">
                {project.priceActivity?.map((event) => (
                  <li key={event.sequence}>{event.customer_message}</li>
                ))}
              </ol>
            </details>
          ) : null}
          {project.status === "estimated" ? (
            <Button
              className="mt-4"
              disabled={busy}
              onClick={() =>
                void run(() =>
                  apiFetch(`/v1/customer/projects/${projectId}/submit`, { method: "POST" }),
                )
              }
            >
              Submit to contractor
            </Button>
          ) : null}
        </Card>
      ) : null}
      {project.submission ? (
        <Card className="border-emerald-200 bg-emerald-50 p-5">
          <p className="font-black text-emerald-950">Submitted to contractor</p>
          <p className="mt-1 text-sm text-emerald-900">
            Submitted {new Date(project.submission.submitted_at).toLocaleDateString()} · Awaiting
            contractor review
          </p>
        </Card>
      ) : null}
      {project.quotes.map((quote) => (
        <Card key={quote.id} className="border-emerald-200 p-5 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-emerald-800">
            Contractor final quote
          </p>
          <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="font-bold">{quote.quote_number}</p>
              <p className="text-2xl font-black">{money(quote.total_minor, quote.currency)}</p>
            </div>
            {quote.readUrl ? (
              <a
                className="text-sm font-bold text-emerald-800 underline"
                href={quote.readUrl}
                target="_blank"
                rel="noreferrer"
              >
                View quote PDF
              </a>
            ) : null}
          </div>
          {quote.scope ? <p className="mt-3 text-sm text-stone-700">{quote.scope}</p> : null}
          {quote.terms ? (
            <p className="mt-2 rounded-lg bg-stone-50 p-3 text-sm text-stone-600">{quote.terms}</p>
          ) : null}
          {quote.status === "issued" ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="text-sm font-bold">
                Signer name
                <input
                  className="mt-1 w-full rounded-lg border border-stone-300 p-2 font-normal"
                  value={signerName}
                  onChange={(event) => setSignerName(event.target.value)}
                />
              </label>
              <label className="text-sm font-bold">
                Decline reason <span className="font-normal text-stone-500">(optional)</span>
                <input
                  className="mt-1 w-full rounded-lg border border-stone-300 p-2 font-normal"
                  value={declineReason}
                  onChange={(event) => setDeclineReason(event.target.value)}
                />
              </label>
              <Button
                disabled={busy || signerName.trim().length < 2}
                onClick={() =>
                  void run(() =>
                    apiFetch(`/v1/customer/quotes/${quote.version_id}/respond`, {
                      method: "POST",
                      body: JSON.stringify({
                        response: "accepted",
                        signerName,
                        acknowledgment: "I understand this is a fixed-price quote.",
                      }),
                    }),
                  )
                }
              >
                Accept fixed-price quote
              </Button>
              <SecondaryButton
                disabled={busy || signerName.trim().length < 2}
                onClick={() =>
                  void run(() =>
                    apiFetch(`/v1/customer/quotes/${quote.version_id}/respond`, {
                      method: "POST",
                      body: JSON.stringify({
                        response: "declined",
                        signerName,
                        declineReason: declineReason.trim() || undefined,
                        acknowledgment: "I understand this is a fixed-price quote.",
                      }),
                    }),
                  )
                }
              >
                Decline quote
              </SecondaryButton>
            </div>
          ) : null}
        </Card>
      ))}
      {actionError ? <p className="text-sm font-semibold text-red-700">{actionError}</p> : null}
      <QuoteReviewModal
        busy={busy}
        error={actionError}
        form={project.intakeState?.quoteForm}
        onClose={() => setReviewOpen(false)}
        onSubmit={async (facts) => {
          await saveQuoteForm(facts);
          setReviewOpen(false);
        }}
        open={reviewOpen}
      />
    </section>
  );
}
